import qs from 'qs';
import fileDownload from 'react-file-download';

import type { CmChecklist, Offer } from '@luxuryescapes/contract-svc-offer';

import type { TagType } from '~/consts/tags';

import type { ScheduleLogsResponse } from '~/types/responses';

import { fetchAndSanitizeSchema } from '~/utils/rjsfValidator';

import { json_headers, request } from './common';

interface QueryParamsProps {
  page: number;
  limit: number;
  id_vendor?: string;
  id_opportunity_salesforce?: string;
}

interface QueryOffersParams {
  page?: number;
  limit?: number;
  queryString?: string;
  region?: string;
  filter?: string;
  type?: string;
  brand?: string;
  orderByScheduleDate?: number;
}

// Types for package target upgrades API response
export interface ScheduleRecord {
  id: number;
  fk_offer_salesforce_id: string;
  start: string;
  end: string;
  type: string;
  region: string;
  brand: string;
}

export interface SearchSchedulesParams {
  type?: string;
  region?: string;
  brand?: string;
  start?: string;
  end?: string;
  /** Match rows overlapping `start`..`end` instead of rows fully inside it. */
  overlaps?: boolean;
  limit?: number;
}

export interface AppExclusiveScheduleWindow {
  scheduleId: number;
  start: string;
  end: string;
}

/** One row of svc-offer `GET /api/schedules/app-exclusive/eligible`. */
export interface EligibleAppExclusiveOffer {
  offerId: string;
  name: string | null;
  location: string | null;
  status: string | null;
  availability: AppExclusiveScheduleWindow;
  onlinePurchase: AppExclusiveScheduleWindow;
  /** svc-offer allows one app_exclusive row per offer and region. */
  existingAppExclusive: AppExclusiveScheduleWindow | null;
  canSchedule: boolean;
}

export type TakenAppExclusive = AppExclusiveScheduleWindow & { offerId: string };

export interface EligibleAppExclusiveOffersResponse {
  region: string;
  date: string;
  /** Length of the slot in days (1 = 24 hours, 2 = 48 hours). Missing on older svc-offer. */
  days?: number;
  slot: { start: string; end: string; onSaleUntil: string };
  /** Every offer's app_exclusive overlapping the slot in this region, earliest first. Missing on older svc-offer. */
  takenBy?: Array<TakenAppExclusive>;
  /** Sydney days (YYYY-MM-DD) of the slot that already hold `maxPerDay` deals. Missing on older svc-offer. */
  fullDates?: Array<string>;
  /** Most deals one region can hold on the same day. Missing on older svc-offer (which allowed one). */
  maxPerDay?: number;
  /** @deprecated Use `takenBy` and `fullDates`. The first deal on the first full day of the slot, or null. */
  dayTakenBy: TakenAppExclusive | null;
  /** Number of eligible offers before `limit` is applied. */
  total: number;
  offers: Array<EligibleAppExclusiveOffer>;
}

export interface EligibleAppExclusiveOffersParams {
  region: string;
  /** Sydney calendar day, YYYY-MM-DD. */
  date: string;
  /** Length of the slot in days (1 = 24 hours, 2 = 48 hours). svc-offer defaults to 1. */
  days?: number;
  q?: string;
  limit?: number;
}

export interface PackageTargetUpgradeDTO {
  id: string;
  target_package_id: string;
  upgrade_type: string;
  base_package_id: string;
  mapping_source: 'default' | 'manual';
}

export interface PackageTargetUpgradesResponse {
  success: boolean;
  total: number;
  result: Array<PackageTargetUpgradeDTO>;
}

export interface OpportunitySnapshotDocument {
  id: string;
  opportunity_id: string;
  name: string;
  filename: string;
  content_type: string | null;
  created_at: string;
  created_by: string | null;
}

export interface LuxPlusPurchaseRestrictionRow {
  id: number;
  offerId: string;
  title: string | null;
  regionCode: string;
  restrictPurchaseToLuxPlusMembers: boolean;
}

export interface LuxPlusPurchaseRestrictionsListParams {
  page: number;
  limit: number;
  region?: string;
  search?: string;
}

export interface LuxPlusScheduleDefault {
  id: string;
  brand: string;
  region: string;
  tier: string;
  enabled: boolean;
  updatedBy: string;
  updatedAt: string;
}

export interface LuxPlusScheduleDefaults {
  scheduleDefaults: Array<LuxPlusScheduleDefault>;
  availableTiers: Array<string>;
}

export interface LuxPlusScheduleDefaultUpdate {
  brand: string;
  region: string;
  tier: string;
  enabled: boolean;
}

interface LuxPlusScheduleDefaultsResponse {
  result: {
    schedule_defaults: Array<{
      id: string;
      brand: string;
      region: string;
      tier: string;
      enabled: boolean;
      updated_by: string;
      updated_at: string;
    }>;
    available_tiers: Array<string>;
  };
}

interface LuxPlusPurchaseRestrictionUpdate {
  ids: Array<number>;
  restrictPurchaseToLuxPlusMembers: boolean;
}

interface UpdateLuxPlusPurchaseRestrictionsPayload {
  updates: Array<LuxPlusPurchaseRestrictionUpdate>;
}

export interface UpdateLuxPlusPurchaseRestrictionsResponse {
  result: {
    updatedCount: number;
    missingIds: Array<number>;
  };
}

export interface LuxPlusPurchaseRestrictionsResponse {
  result: {
    rows: Array<LuxPlusPurchaseRestrictionRow>;
    total: number;
    page: number;
    limit: number;
  };
}

// Enriched type for display in the DataGrid table

export type PackageTargeUpgradesMappingSource = 'Default' | 'Manual';
export interface PackageUpgradeTableRow {
  id: string;
  basePackageName: string;
  targetPackageName: string;
  basePackageId: string; // Keep IDs for reference
  targetPackageId: string;
  incrementalCostPercent: number;
  upgradeType: 'Package or room upgrade' | 'Room type upgrade' | 'Package type upgrade';
  manualOrDefault: PackageTargeUpgradesMappingSource;
}

export interface CMChecklistManualOverride {
  id: string;
  offer_id: string;
  item_key: string;
  evidence_hash: string;
  is_approved: boolean;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CMChecklistManualOverrideUpsert {
  evidence_hash: string;
  is_approved: boolean;
  item_key: string;
  updated_by: string;
}

interface createOfferPackagesRequestBody {
  property_id: string;
  offer_id_salesforce_external: string;
  create_missing?: boolean;
}

type PackageUpgradeType = 'room' | 'package';

export type AccountAssetRecordType = 'rooms' | 'services';

export interface ExperienceDealOptionsParams {
  deal_sub_category: string;
  destination?: string | null;
  sub_destination?: string | null;
  travel_from?: string | null;
  search?: string;
  page: number;
  limit: number;
}

export interface SalesforceAccount {
  id: string;
  name: string;
}

interface autoMapPackageUpgradesRequestBody {
  upgrade_types: Array<PackageUpgradeType>;
}

export default class OffersService {
  static basePath() {
    return window.configs.API_HOST + '/api/offers';
  }

  static tagsPath() {
    return window.configs.API_HOST + '/api/offers/tags';
  }

  static contentPath() {
    return window.configs.API_HOST + '/api/offers-content';
  }

  static customOffersPath() {
    return window.configs.API_HOST + '/api/offers/custom';
  }

  static searchPath() {
    return window.configs.BOOKING_FLOW_V2 === 'true'
      ? window.configs.API_HOST + '/api/offers'
      : window.configs.API_HOST + '/api/offers-search';
  }
  static offerAddonsPath() {
    return window.configs.API_HOST + '/api/offers/addons';
  }
  static getPackagesBulkCreatePath() {
    return `${window.configs.API_HOST}/api/offers/packages/create-bulk`;
  }
  static vendorAddonsPath() {
    return window.configs.API_HOST + '/api/vendor-addons';
  }

  static luxPlusPurchaseRestrictionsPath() {
    return `${this.basePath()}/lux-plus/purchase-restrictions`;
  }

  static getLuxPlusPurchaseRestrictions(
    params: LuxPlusPurchaseRestrictionsListParams,
    options?: { signal?: AbortSignal },
  ): Promise<LuxPlusPurchaseRestrictionsResponse> {
    const query = qs.stringify({
      page: params.page,
      limit: params.limit,
      region: params.region || undefined,
      search: params.search || undefined,
    });

    return request(`${this.luxPlusPurchaseRestrictionsPath()}?${query}`, {
      method: 'GET',
      signal: options?.signal,
    });
  }

  static updateLuxPlusPurchaseRestrictions(
    payload: UpdateLuxPlusPurchaseRestrictionsPayload,
  ): Promise<UpdateLuxPlusPurchaseRestrictionsResponse> {
    return request(this.luxPlusPurchaseRestrictionsPath(), {
      method: 'PATCH',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  // Nested under /api/offers because the API gateway only forwards a fixed set of prefixes to
  // svc-offer. A sibling path 404s at the gateway and never reaches the service.
  static luxPlusScheduleDefaultsPath() {
    return `${this.basePath()}/lux-plus/schedule-defaults`;
  }

  static async getLuxPlusScheduleDefaults(options?: { signal?: AbortSignal }): Promise<LuxPlusScheduleDefaults> {
    const response: LuxPlusScheduleDefaultsResponse = await request(this.luxPlusScheduleDefaultsPath(), {
      method: 'GET',
      signal: options?.signal,
    });

    return {
      scheduleDefaults: response.result.schedule_defaults.map((row) => ({
        id: row.id,
        brand: row.brand,
        region: row.region,
        tier: row.tier,
        enabled: row.enabled,
        updatedBy: row.updated_by,
        updatedAt: row.updated_at,
      })),
      availableTiers: response.result.available_tiers,
    };
  }

  static updateLuxPlusScheduleDefaults(updates: Array<LuxPlusScheduleDefaultUpdate>): Promise<unknown> {
    return request(this.luxPlusScheduleDefaultsPath(), {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify({ updates }),
    });
  }

  static getAllLocations() {
    return request(`${this.basePath()}/locations`, {
      method: 'GET',
      credentials: 'omit',
    });
  }

  static getOfferStatuses(offerIds: Array<string>): Promise<{ result: Record<string, string | null> }> {
    return request(`${this.basePath()}/statuses`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ offer_ids: offerIds }),
    });
  }

  static getTags(type) {
    return request(`${this.tagsPath()}/${type}`, { method: 'GET' });
  }

  static createTag(type, name) {
    return request(`${this.tagsPath()}/${type}`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ name }),
    });
  }

  static updateTag(type: TagType, id: number, name: string) {
    return request(`${this.tagsPath()}/${type}/${id}`, {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify({ name }),
    });
  }

  static deleteTag(type: TagType, id: number) {
    return request(`${this.tagsPath()}/${type}/${id}`, {
      method: 'DELETE',
      headers: json_headers,
      ignoreResponse: true,
    });
  }

  // Custom offers
  static getCustomOffers({ customerId: customer_id, page = 1, perPage = 25 }) {
    const queryParams = {
      page,
      perPage,
      size: perPage,
      customer_id,
    };

    return request(`${this.customOffersPath()}?${qs.stringify(queryParams)}`, {
      method: 'GET',
    });
  }

  static getCustomOffer(offerId: string, options?: { include_payment_metadata?: boolean }) {
    return request(`${this.customOffersPath()}/${offerId}?${qs.stringify(options)}`, {
      method: 'GET',
    });
  }

  static createCustomOffer(payload) {
    return request(`${this.customOffersPath()}`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static updateCustomOffer(offerId: string, payload) {
    return request(`${this.customOffersPath()}/${offerId}`, {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static deleteCustomOffer(offerId: string) {
    return request(`${this.customOffersPath()}/${offerId}`, {
      method: 'DELETE',
      ignoreResponse: true,
    });
  }

  static setCustomOfferStatus(offerId: string, status: App.CustomOffer['status']) {
    return request(`${this.customOffersPath()}/${offerId}/status`, {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify({ status }),
    });
  }

  // TODO: Export this from svc-offer contract
  static getCustomOfferVendorVirtualCreditCards(customOfferId: string) {
    // Check balance flag fetches realtime balance of the card.
    // if not provided, it will return the balance recorded at svc-vcc level, which will wrong if the card has been charged by a third party.
    return request(`${this.customOffersPath()}/${customOfferId}/virtual-credit-cards?check_balance=true`, {
      method: 'GET',
    });
  }

  // TODO: Export this from svc-offer contract
  static reserveVirtualCreditCardsForCustomOffer(customOfferId: string) {
    return request(`${this.customOffersPath()}/${customOfferId}/virtual-credit-cards`, {
      method: 'POST',
      headers: json_headers,
    });
  }

  static getCustomOfferVirtualCreditCardsHistory(customOfferId: string) {
    return request(`${this.customOffersPath()}/${customOfferId}/virtual-credit-cards/${customOfferId}/history`, {
      method: 'GET',
    });
  }

  // Offers
  static getOffers({
    page = 1,
    limit = 15,
    queryString,
    region,
    filter,
    brand,
    type,
    orderByScheduleDate,
  }: QueryOffersParams) {
    const queryParams = {
      page,
      limit,
      q: queryString,
      region,
      filter,
      type,
      brand,
      orderByScheduleDate,
    };

    return request(`${this.basePath()}?${qs.stringify(queryParams)}`, {
      method: 'GET',
    });
  }

  static getFilterOffers({ page = 1, limit = 15, queryString, filter }) {
    let queryParams;

    if (filter) {
      queryParams = {
        page,
        limit,
        filter,
      };
    } else {
      queryParams = {
        page,
        limit,
      };
    }

    return request(`${this.basePath()}?${qs.stringify(queryParams)}&${queryString}`, {
      method: 'GET',
    });
  }

  static getAllAddons({ vendorId, addonOpportunityId, page = 1, limit = 30 }) {
    const queryParams: QueryParamsProps = { page, limit };

    if (vendorId) {
      queryParams.id_vendor = vendorId;
    }

    if (addonOpportunityId) {
      queryParams.id_opportunity_salesforce = addonOpportunityId;
    }

    return request(`${this.basePath()}/addons/all?${qs.stringify(queryParams)}`, { method: 'GET' });
  }

  static searchOffersByType(query: string, type: string) {
    const params = qs.stringify({ q: query, type, brand: 'luxuryescapes', limit: 1 });
    return request(`${this.basePath()}?${params}`, { method: 'GET' });
  }

  static searchOffers({ queryString, brand }) {
    if (window.configs.BOOKING_FLOW_V2 !== 'true') {
      const queryParams = {
        q: queryString,
        brand,
      };

      return request(`${this.searchPath()}?${qs.stringify(queryParams)}`, {
        method: 'GET',
      });
    }

    const searchQueryString = queryString.includes('brand=') ? queryString : `${queryString}&brand=${brand}`;

    return request(`${this.searchPath()}?${searchQueryString}&limit=10`, {
      method: 'GET',
    });
  }

  static getOfferSchema(id) {
    return fetchAndSanitizeSchema(this.basePath() + '/' + id);
  }

  static getOffer(id, options?) {
    let params = '';
    if (options) {
      params = Object.entries(options)
        .map(([key, val]: [string, string]) => `${key}=${encodeURIComponent(val)}`)
        .join('&');
    }

    return (
      request(this.basePath() + `/${id}?${params}`, {
        method: 'GET',
      })
        .then((response) => {
          response.result.packages = response.result.packages.map((p) => ({
            ...p,
            addons: p.addons || [],
            unique_key: p.id_salesforce_external,
          }));
          return response;
        })
        // tests not playing nice
        .catch()
    );
  }

  static getOfferLocalization(offerId) {
    return request(this.basePath() + `/${offerId}/localization`, {
      method: 'GET',
    });
  }

  static getOfferTemplates() {
    return request(this.basePath() + '/templates/all', {
      method: 'GET',
    });
  }

  static upsertOfferTemplate(template: App.OfferTemplate) {
    //id is autoincremented integer starting at 1.
    //if no collision is found on id then
    //a new row will be created
    const id = template.id ? template.id : 0;
    const jsonData = JSON.stringify(template);
    return request(this.basePath() + '/templates/' + id, {
      method: 'PUT',
      body: jsonData,
      headers: json_headers,
    });
  }

  static deleteOfferTemplate(id: number) {
    return request(this.basePath() + '/templates/' + id, {
      method: 'DELETE',
    });
  }

  static generateRoomPolicy(
    deal_offer_id: string,
    property_id: string,
    room_type_id: string,
    room_rate_id: string,
    room_rate_ids?: Array<string>,
    acceptLanguage: 'en' | 'de' | 'fr' = 'en',
  ) {
    return request(this.basePath() + '/rooms/room-policy-generation', {
      method: 'POST',
      headers: { ...json_headers, 'Accept-Language': acceptLanguage },
      body: JSON.stringify({ deal_offer_id, property_id, room_type_id, room_rate_id, room_rate_ids }),
    });
  }

  static getRoomPolicy(
    offerIdSalesforceExternal: string,
    roomPolicyId: number,
    acceptLanguage: 'en' | 'de' | 'fr' = 'en',
  ) {
    return request(`${this.roomPoliciesUri(offerIdSalesforceExternal)}/${roomPolicyId}`, {
      method: 'GET',
      headers: { 'Accept-Language': acceptLanguage },
    });
  }

  static generateBeddingConfiguration(deal_id: string) {
    return request(`${this.basePath()}/${deal_id}/rooms/bedding-configurations`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ deal_id }),
    });
  }

  static generateBulkBeddingConfigurations(deal_offer_ids: Array<string>) {
    return request(`${this.basePath()}/rooms/bedding-configurations/bulk`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ deal_offer_ids }),
    });
  }

  static getPackage(idOffer, id) {
    return request(`${this.basePath()}/${idOffer}/packages/${id}`, {
      method: 'GET',
    });
  }

  static deletePackage(idOffer, id) {
    return request(`${this.basePath()}/${idOffer}/packages/${id}`, {
      method: 'DELETE',
    });
  }

  static getAddon(idOffer, idPackage, idAddon) {
    return request(`${this.basePath()}/${idOffer}/packages/${idPackage}/addons/${idAddon}`, {
      method: 'GET',
    });
  }

  static getPackageAddons(idOffer: string, idPackage: string): Promise<{ result: Array<App.Addon> }> {
    return request(`${this.basePath()}/${idOffer}/packages/${idPackage}/addons`, {
      method: 'GET',
    });
  }

  static createOffer(object) {
    const jsonData = JSON.stringify(object);
    return request(this.basePath(), {
      method: 'POST',
      headers: json_headers,
      body: jsonData,
    });
  }

  static updateOffer(object, id, options?: { acceptLanguage?: string }) {
    const jsonData = JSON.stringify(object);
    return request(this.basePath() + '/' + id, {
      method: 'PATCH',
      headers: options?.acceptLanguage ? { ...json_headers, 'Accept-Language': options.acceptLanguage } : json_headers,
      body: jsonData,
    });
  }

  static async updateOfferContentLocalization(offerId, fields) {
    const data = JSON.stringify({ localizationStrings: fields });
    const basePath = this.basePath();

    return request(`${basePath}/${offerId}/localization`, {
      method: 'PUT',
      headers: json_headers,
      body: data,
    });
  }

  static schedulesUri(idOffer) {
    return `${this.basePath()}/${idOffer}/schedules`;
  }

  static scheduleDataMigrationUri(idOffer) {
    return `${this.basePath()}/${idOffer}/scheduleDataMigration`;
  }

  static updateSchedule(object, idOffer, id) {
    const jsonData = JSON.stringify(object);
    return request(`${this.schedulesUri(idOffer)}/${id}`, {
      method: 'PATCH',
      headers: json_headers,
      body: jsonData,
    });
  }

  static migrateScheduleData(object, idOffer) {
    const jsonData = JSON.stringify(object);
    return request(`${this.scheduleDataMigrationUri(idOffer)}`, {
      method: 'POST',
      headers: json_headers,
      body: jsonData,
    });
  }

  static updateScheduleList(object, idOffer) {
    const jsonData = JSON.stringify(object);
    return request(`${this.schedulesUri(idOffer)}`, {
      method: 'PATCH',
      headers: json_headers,
      body: jsonData,
    });
  }

  static createScheduleList(object, idOffer) {
    const jsonData = JSON.stringify(object);
    return request(`${this.schedulesUri(idOffer)}`, {
      method: 'PUT',
      headers: json_headers,
      body: jsonData,
    });
  }

  static createSchedule(object, idOffer) {
    const jsonData = JSON.stringify(object);
    return request(`${this.schedulesUri(idOffer)}`, {
      method: 'POST',
      headers: json_headers,
      body: jsonData,
    });
  }

  static deleteSchedule(idOffer, id) {
    return request(`${this.schedulesUri(idOffer)}/${id}`, {
      method: 'DELETE',
      ignoreResponse: true,
    });
  }

  static deleteScheduleList(idOffer, object) {
    const jsonData = JSON.stringify(object);
    return request(`${this.schedulesUri(idOffer)}`, {
      method: 'DELETE',
      ignoreResponse: true,
      headers: json_headers,
      body: jsonData,
    });
  }

  static getSchedules(idOffer) {
    return request(this.schedulesUri(idOffer), { method: 'GET' });
  }

  /**
   * Cross-offer schedule search (svc-offer `GET /api/schedules`). Returns raw `public.schedule` rows,
   * so callers look up offer details separately. svc-offer filters `start >= start` and `end < end`,
   * and replies 204 (which `request` resolves as `true`) when nothing matches.
   */
  static async searchSchedules(
    params: SearchSchedulesParams,
    { signal }: { signal?: AbortSignal } = {},
  ): Promise<Array<ScheduleRecord>> {
    const response = await request(`${window.configs.API_HOST}/api/schedules?${qs.stringify(params)}`, {
      method: 'GET',
      signal,
    });
    return Array.isArray(response) ? response : [];
  }

  /** Offline flash hotels that can take the App Only Flash (`app_exclusive`) slot on a Sydney day. */
  static getEligibleAppExclusiveOffers(
    params: EligibleAppExclusiveOffersParams,
    { signal }: { signal?: AbortSignal } = {},
  ): Promise<EligibleAppExclusiveOffersResponse> {
    return request(`${window.configs.API_HOST}/api/schedules/app-exclusive/eligible?${qs.stringify(params)}`, {
      method: 'GET',
      signal,
    });
  }

  static getCMChecklistEvaluation(
    offerIdSalesforceExternal: string,
    { signal }: { signal?: AbortSignal } = {},
  ): Promise<{ result: CmChecklist.Evaluation }> {
    return request(`${this.basePath()}/${offerIdSalesforceExternal}/cm-checklist/evaluation`, {
      method: 'GET',
      signal,
    });
  }

  static getCMChecklistPolicyBible({ signal }: { signal?: AbortSignal } = {}): Promise<{
    result: CmChecklist.PolicyBible;
  }> {
    return request(`${this.basePath()}/cm-checklist/policy-bible`, { method: 'GET', signal });
  }

  // The server evaluates the offer and picks what to delete; the request carries no payload.
  static reconcileCMChecklist(offerIdSalesforceExternal: string): Promise<{ result: CmChecklist.ReconcileResult }> {
    return request(`${this.basePath()}/${offerIdSalesforceExternal}/cm-checklist/manual-overrides/reconcile`, {
      method: 'POST',
    });
  }

  static upsertCMChecklistManualOverride(offerIdSalesforceExternal: string, payload: CMChecklistManualOverrideUpsert) {
    return request(`${this.basePath()}/${offerIdSalesforceExternal}/cm-checklist/manual-overrides`, {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static getScheduleSchema(idOffer) {
    return fetchAndSanitizeSchema(this.schedulesUri(idOffer));
  }

  static getScheduleLogs(
    idOffer: string,
    queryParams: { type?: string; brand?: string },
  ): Promise<ScheduleLogsResponse> {
    return request(`${this.basePath()}/${idOffer}/schedule-logs?${qs.stringify(queryParams)}`, {
      method: 'GET',
    });
  }

  static imagesUri(idOffer) {
    return `${this.basePath()}/${idOffer}/images`;
  }

  static imageUri(idOffer, idImage) {
    return `${this.basePath()}/${idOffer}/images/${idImage}`;
  }

  static getImages(idOffer) {
    return request(this.imagesUri(idOffer), { method: 'GET' });
  }

  static createImage(object, idOffer) {
    const jsonData = JSON.stringify(object);
    return request(this.imagesUri(idOffer), {
      method: 'POST',
      headers: json_headers,
      body: jsonData,
    });
  }

  static updateImage(idOffer, idImage, data) {
    const jsonData = JSON.stringify(data);
    return request(this.imageUri(idOffer, idImage), {
      method: 'PATCH',
      headers: json_headers,
      body: jsonData,
    });
  }

  static updateImages(objects, idOffer) {
    const jsonData = JSON.stringify(objects);
    return request(this.imagesUri(idOffer), {
      method: 'PATCH',
      headers: json_headers,
      body: jsonData,
    });
  }

  static deleteImage(idOffer, id) {
    return request(`${this.imagesUri(idOffer)}/${id}`, { method: 'DELETE' });
  }

  static getVendorAddons(vendorId) {
    return request(`${this.vendorAddonsPath()}/${vendorId}`, { method: 'GET' });
  }

  static getVendorAddon(vendorId, addonId) {
    return request(`${this.vendorAddonsPath()}/${vendorId}/opportunities/${addonId}`, { method: 'GET' });
  }

  static getVendorAddonOption(vendorId, addonId, addonOptionId) {
    return request(`${this.vendorAddonsPath()}/${vendorId}/opportunities/${addonId}/deal-options/${addonOptionId}`, {
      method: 'GET',
    });
  }

  static getVendorAddonOptions({ vendorId, addonOpportunityId, page = 1, limit = 30 }) {
    const queryParams = { page, limit };

    return request(
      `${this.vendorAddonsPath()}/${vendorId}/opportunities/${addonOpportunityId}/deal-options?${qs.stringify(
        queryParams,
      )}`,
      { method: 'GET' },
    );
  }

  static addonImagesUri(addonSalesforceId) {
    return `${this.offerAddonsPath()}/${addonSalesforceId}/images`;
  }

  static addonImageUri(addonSalesforceId, imageId) {
    return `${this.offerAddonsPath()}/${addonSalesforceId}/images/${imageId}`;
  }

  static getAddonImages(addonSalesforceId) {
    return request(this.addonImagesUri(addonSalesforceId), { method: 'GET' });
  }

  static createAddonImage(addonSalesforceId, data) {
    const jsonData = JSON.stringify(data);
    return request(this.addonImagesUri(addonSalesforceId), {
      method: 'POST',
      headers: json_headers,
      body: jsonData,
    });
  }

  static updateAddonImage(addonSalesforceId, imageId, data) {
    const jsonData = JSON.stringify(data);
    return request(this.addonImageUri(addonSalesforceId, imageId), {
      method: 'PATCH',
      headers: json_headers,
      body: jsonData,
    });
  }

  static updateAddonImages(addonSalesforceId, data) {
    const jsonData = JSON.stringify(data);
    return request(this.addonImagesUri(addonSalesforceId), {
      method: 'PATCH',
      headers: json_headers,
      body: jsonData,
    });
  }

  static deleteAddonImage(addonSalesforceId, imageId) {
    return request(`${this.addonImageUri(addonSalesforceId, imageId)}`, {
      method: 'DELETE',
      ignoreResponse: true,
    });
  }

  static roomPoliciesUri(idOffer) {
    return `${this.basePath()}/${idOffer}/room-policies`;
  }

  static getRoomPolicySchema(idOffer) {
    return fetchAndSanitizeSchema(this.roomPoliciesUri(idOffer));
  }

  static createRoomPolicy(object, idOffer, acceptLanguage: 'en' | 'de' | 'fr' = 'en') {
    const jsonData = JSON.stringify(object);
    return request(`${this.roomPoliciesUri(idOffer)}`, {
      method: 'POST',
      headers: { ...json_headers, 'Accept-Language': acceptLanguage },
      body: jsonData,
    });
  }

  static updateRoomPolicy(object, idOffer, idRoomPolicy, acceptLanguage: 'en' | 'de' | 'fr' = 'en') {
    const jsonData = JSON.stringify(object);
    return request(`${this.roomPoliciesUri(idOffer)}/${idRoomPolicy}`, {
      method: 'PATCH',
      headers: { ...json_headers, 'Accept-Language': acceptLanguage },
      body: jsonData,
    });
  }

  static createOfferPackages(object: createOfferPackagesRequestBody) {
    const jsonData = JSON.stringify(object);
    return request(`${this.getPackagesBulkCreatePath()}`, {
      method: 'POST',
      headers: json_headers,
      body: jsonData,
    });
  }

  /**
   * Primarily used with LuxLoyalty to map package upgrades. This then enables
   * complementary upgrades for members.
   * @deprecated use mapPackageTargetUpgrades instead. NOTE - temporarily we will auto-map using the old endpoint
   * and the new endpoint to test the new endpoint works as expected. When we are confident the new endpoint works as expected,
   * we will remove the old endpoint.
   */
  static mapPackageUpgrades(idOffer: string) {
    return request(`${this.basePath()}/${idOffer}/map-package-upgrades`, {
      method: 'POST',
      headers: json_headers,
    });
  }

  /**
   * Primarily used with LuxLoyalty to map package upgrades. This then enables
   * complementary upgrades for members.
   */
  static mapPackageTargetUpgrades(idOffer: string, data: autoMapPackageUpgradesRequestBody) {
    return request(`${this.basePath()}/${idOffer}/package-target-upgrades/auto-map`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(data),
    });
  }

  static getOpportunities(
    { vendorId, page, limit }: { vendorId: string; page: number; limit: number },
    options: { signal?: AbortSignal } = {},
  ) {
    return request(`${this.basePath()}/opportunities?${qs.stringify({ vendor_id: vendorId, page, limit })}`, {
      method: 'GET',
      signal: options.signal,
    });
  }

  static getOpportunity(opportunityId: string) {
    return request(`${this.basePath()}/opportunities/${opportunityId}`, {
      method: 'GET',
    });
  }

  static listOpportunityDocuments(opportunityId: string): Promise<{ result: OpportunitySnapshotDocument[] }> {
    return request(`${this.basePath()}/opportunities/${opportunityId}/documents`, {
      method: 'GET',
    });
  }

  static uploadOpportunityDocument(
    opportunityId: string,
    payload: { name: string; filename: string; contentType: string; fileBase64: string },
  ) {
    return request(`${this.basePath()}/opportunities/${opportunityId}/documents`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({
        name: payload.name,
        filename: payload.filename,
        content_type: payload.contentType,
        file_base64: payload.fileBase64,
      }),
    });
  }

  static async downloadOpportunityDocument(opportunityId: string, documentId: string, filename: string) {
    const blob = await request(`${this.basePath()}/opportunities/${opportunityId}/documents/${documentId}/download`, {
      method: 'GET',
      download: true,
    });
    fileDownload(blob, filename);
  }

  static deleteOpportunityDocument(opportunityId: string, documentId: string) {
    return request(`${this.basePath()}/opportunities/${opportunityId}/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  static crmResyncOpportunity(opportunityId: string) {
    return request(`${this.basePath()}/opportunities/${opportunityId}/crm-resync`, {
      method: 'POST',
    });
  }

  static getCrmResyncStatus(opportunityId: string, requestedAt: string) {
    return request(
      `${this.basePath()}/opportunities/${opportunityId}/crm-resync/status?${qs.stringify({ requestedAt })}`,
      { method: 'GET' },
    );
  }

  static getCampaignScheduleImportCandidates(
    offerId: string,
    options: { signal?: AbortSignal; selectedKeys?: Array<string> } = {},
  ) {
    // Repeated selectedKeys= params, matching the backend's normalizeSelectedKeysQuery.
    const query = (options.selectedKeys ?? []).map((key) => `selectedKeys=${encodeURIComponent(key)}`).join('&');
    const url = `${this.basePath()}/${offerId}/campaign-schedules/import${query ? `?${query}` : ''}`;
    return request(url, {
      method: 'GET',
      signal: options.signal,
    });
  }

  static importCampaignSchedules(offerId: string, selectedKeys: Array<string>) {
    return request(`${this.basePath()}/${offerId}/campaign-schedules/import`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ selectedKeys }),
    });
  }

  static getOpportunityRelated(opportunityId: string) {
    return request(`${this.basePath()}/opportunities/${opportunityId}/related`, {
      method: 'GET',
    });
  }

  static getAccountOfferIds(
    accountId: string,
    { page, limit, signal }: { page: number; limit: number; signal?: AbortSignal },
  ): Promise<{ result: Array<{ id_salesforce_external: string }>; total: number }> {
    const query = qs.stringify({ q: accountId, onlyIds: true, page, limit });
    return request(`${this.basePath()}?${query}`, {
      method: 'GET',
      signal,
    });
  }

  static getAccount(
    accountId: string,
    { signal }: { signal?: AbortSignal } = {},
  ): Promise<{ result: SalesforceAccount }> {
    return request(`${this.basePath()}/accounts/${accountId}`, {
      method: 'GET',
      signal,
    });
  }

  static getAccountAssets(
    accountId: string,
    { recordType, signal }: { recordType?: AccountAssetRecordType; signal?: AbortSignal } = {},
  ): Promise<Offer.GetAccountAssetsResponse> {
    const query = recordType ? `?record_type=${recordType}` : '';
    return request(`${this.basePath()}/accounts/${accountId}/assets${query}`, {
      method: 'GET',
      signal,
    });
  }

  static getExperienceDealOptions(
    params: ExperienceDealOptionsParams,
    { signal }: { signal?: AbortSignal } = {},
  ): Promise<Offer.GetExperienceDealOptionsResponse> {
    const query = qs.stringify(params, { skipNulls: true });
    return request(`${this.basePath()}/experience-deal-options?${query}`, {
      method: 'GET',
      signal,
    });
  }

  static getPricingToolDraft(opportunityId: string) {
    return request(`${this.basePath()}/opportunities/${opportunityId}/pricing-tool/draft`, {
      method: 'GET',
    });
  }

  static savePricingToolDraft(opportunityId: string, version: number, payload: unknown) {
    return request(`${this.basePath()}/opportunities/${opportunityId}/pricing-tool/draft`, {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify({ version, payload }),
    });
  }

  static deletePricingToolDraft(opportunityId: string, version: number) {
    return request(`${this.basePath()}/opportunities/${opportunityId}/pricing-tool/draft`, {
      method: 'DELETE',
      headers: json_headers,
      body: JSON.stringify({ version }),
    });
  }

  static getPricingToolSubmissions(opportunityId: string) {
    return request(`${this.basePath()}/opportunities/${opportunityId}/pricing-tool/submissions`, { method: 'GET' });
  }

  static submitPricingTool(opportunityId: string, body: unknown) {
    return request(`${this.basePath()}/opportunities/${opportunityId}/pricing-tool/submissions`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(body),
    });
  }

  static getDeals(opportunityId: string, page = 1, limit = 10) {
    return request(`${this.basePath()}/opportunities/deals/${opportunityId}?page=${page}&limit=${limit}`, {
      method: 'GET',
    });
  }

  static createOfferDeepLink(offerId: string, params: { [key: string]: unknown }) {
    const jsonData = JSON.stringify(params);
    return request(`${this.basePath()}/${offerId}/deep-links`, {
      method: 'POST',
      headers: json_headers,
      body: jsonData,
    });
  }
  static getPackageTargetUpgrades(offerIdSalesforceExternal: string): Promise<PackageTargetUpgradesResponse> {
    return request(`${this.basePath()}/${offerIdSalesforceExternal}/package-target-upgrades?mapping_source=true`, {
      method: 'GET',
    });
  }
  static getOffersDeepLinks(page: number) {
    return request(`${this.basePath()}/deep-links/all?page=${page + 1}`, {
      method: 'GET',
    });
  }
}
